import { Button, ButtonVariant } from "components/Form/Button";
import { Input } from "components/Form/Input";
import { Layout } from "components/Layout";
import { Field } from "components/Form/Field";
import { memo } from "react";
import { AppRoutes } from "types";
import { ReactComponent as Ai } from "assets/ai.svg";
import { UseFormRegister, FieldErrors } from "react-hook-form";

type EmailFormValue = {
  email: string;
};

interface EmailStepProps {
  step: "email" | "confirm" | "newEmail";
  originalEmail: string;
  newEmail: string;
  processing: boolean;
  isEmailValid: boolean;
  emailErrors: FieldErrors<EmailFormValue>;
  emailLinkError: string;
  register: UseFormRegister<EmailFormValue>;
  onEmailSubmit: (e: React.FormEvent) => void;
  onContinueWithOriginal: () => void;
  onLinkNewEmail: () => void;
  onConfirmNewEmail: () => void;
  onGoBackFromNewEmail: () => void;
  onGoBackToEmailInput: () => void;
  setNewEmail: (email: string) => void;
}

export const EmailStep = memo(function EmailStep({
  step,
  originalEmail,
  newEmail,
  processing,
  isEmailValid,
  emailErrors,
  emailLinkError,
  register,
  onEmailSubmit,
  onContinueWithOriginal,
  onLinkNewEmail,
  onConfirmNewEmail,
  onGoBackFromNewEmail,
  onGoBackToEmailInput,
  setNewEmail,
}: EmailStepProps) {
  // Step 1: Email Input
  if (step === "email") {
    return (
      <form className="contents" onSubmit={onEmailSubmit}>
        <Layout
          className="h-full text-center"
          toolbar={
            <>
              <Button disabled={!isEmailValid || processing} type="submit">
                {processing ? "Loading..." : "Get Started"}
              </Button>

              <Button
                to={AppRoutes.signup}
                variant={ButtonVariant.FLAT}
                type="button"
              >
                Create new ContributorID
              </Button>
            </>
          }
        >
          <div className="grid h-full space-y-12">
            <div className="grid self-start justify-items-center">
              <Ai />

              <h1 className="text-24 leading-1.2 font-semibold mt-4">
                Set ContributorID Password
              </h1>

              <p className="text-858494 mt-3">
                Enter the email address associated with your account to set up
                your ContributorID password.
              </p>
            </div>

            <div className="text-left space-y-8">
              <Field
                label="Use the email linked to your existing contributions. If you're new, choose 'Create a new ContributorID' below."
                description={emailErrors.email?.message}
                descriptionClassName={
                  emailErrors.email?.message ? "text-fd380f" : undefined
                }
                as="label"
              >
                <Input
                  type="email"
                  id="email"
                  autoCapitalize="off"
                  autoComplete="email"
                  autoFocus
                  disabled={processing}
                  placeholder="your.email@example.com"
                  {...register("email", { required: true })}
                />
              </Field>
            </div>
          </div>
        </Layout>
      </form>
    );
  }

  // Step 2: Email Confirmation
  if (step === "confirm") {
    return (
      <Layout
        className="h-full flex items-start justify-center pt-12"
        toolbar={
          <Button
            variant={ButtonVariant.FLAT}
            type="button"
            onClick={onGoBackToEmailInput}
          >
            Back
          </Button>
        }
      >
        <div className="w-full max-w-md px-6 space-y-8">
          <div className="text-center">
            <Ai className="mx-auto" />

            <h1 className="text-24 leading-1.2 font-semibold mt-4">
              Verify Your Email
            </h1>

            <div className="space-y-3 mt-6">
              <p className="text-858494">You are logged in as</p>
              <p className="text-18 font-semibold text-gray-900">
                {originalEmail}
              </p>
              <p className="text-858494 mt-6">
                Would you like to proceed with this email or link this account
                to a new one? Please make sure you have access to the email you
                use for your account.
              </p>
              {emailLinkError && (
                <p className="text-fd380f text-14 mt-4">{emailLinkError}</p>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <Button
              type="button"
              onClick={onContinueWithOriginal}
              className="w-full"
              disabled={processing}
            >
              {processing
                ? "Sending code..."
                : `Continue with ${originalEmail}`}
            </Button>

            <Button
              variant={ButtonVariant.DEFAULT}
              type="button"
              onClick={onLinkNewEmail}
              className="w-full"
              disabled={processing}
            >
              Continue with a new email
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  // Step 2b: Enter New Email for Linking
  if (step === "newEmail") {
    return (
      <Layout
        className="h-full flex items-start justify-center pt-12"
        toolbar={
          <>
            <Button
              disabled={!newEmail || processing}
              type="button"
              onClick={onConfirmNewEmail}
            >
              {processing ? "Sending code..." : "Continue"}
            </Button>

            <Button
              variant={ButtonVariant.FLAT}
              type="button"
              onClick={onGoBackFromNewEmail}
              disabled={processing}
            >
              Back
            </Button>
          </>
        }
      >
        <div className="w-full max-w-md px-6 space-y-6">
          <div className="text-center">
            <Ai className="mx-auto" />

            <h1 className="text-24 leading-1.2 font-semibold mt-4">
              Link a New Email
            </h1>

            <div className="space-y-2 mt-6">
              <p className="text-858494">Your account with</p>
              <p className="text-18 font-semibold text-gray-900">
                {originalEmail}
              </p>
              <p className="text-858494 mt-4">
                will be linked to the new email you provide below.
              </p>
              <p className="text-14 text-858494 mt-4">
                You'll use the new email for login and verification, but all
                your contributions will remain associated with {originalEmail}.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <Field
              label="Enter your new email address"
              as="label"
              description={emailLinkError}
              descriptionClassName={emailLinkError ? "text-fd380f" : undefined}
            >
              <Input
                type="email"
                id="new-email"
                autoCapitalize="off"
                autoComplete="email"
                autoFocus
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="your.new.email@example.com"
              />
            </Field>
          </div>
        </div>
      </Layout>
    );
  }

  return null;
});
